<!-- Responsibility: Show contextual onboarding cards and AI-setup reminders.
Scope: Tutorial hint visibility/actions only; no scene gameplay state management. -->
<tutorial-overlay>
  <!-- AI setup warning: shown when game is active but no AI provider configured -->
  <div if={ state.mode === 'aiSetup' } class="tut-card tut-center { state.suppressed ? 'is-suppressed' : '' }" role="alertdialog" aria-live="assertive" onclick={ onOverlayClick }>
    <div class="tut-header">
      <span class="tut-badge">Setup required</span>
      <button class="tut-close" data-action="dismissAI" data-tip="Dismiss">✕</button>
    </div>
    <h4 class="tut-title">AI provider needed</h4>
    <p class="tut-body">No AI provider detected. Open <span class="tut-inline">AI SETTINGS</span> and start Ollama locally or add a free Gemini API key (<span class="tut-inline-link">aistudio.google.com</span>). Gemini is also needed for scene images.</p>
    <div class="tut-actions">
      <button class="action-btn primary tut-action-btn" data-action="openSettings">Open settings</button>
      <button class="action-btn tut-action-btn" data-action="dismissAI">Later</button>
    </div>
  </div>

  <!-- Step-by-step onboarding: shown on first game only -->
  <div if={ state.mode === 'onboarding' } class="tut-card tut-onboarding { state.suppressed ? 'is-suppressed' : '' }" role="dialog" aria-label="Welcome" onclick={ onOverlayClick }>
    <div class="tut-header">
      <span class="tut-badge">Welcome</span>
      <span class="tut-step-count">{ state.step + 1 } / { onboardingSteps().length }</span>
      <button class="tut-close" data-action="finishOnboarding" data-tip="Skip">✕</button>
    </div>
    <div class="tut-step">
      <h4 class="tut-step-title">{ currentStep().title }</h4>
      <p class="tut-step-body">{ currentStep().body }</p>
    </div>
    <div class="tut-actions">
      <button if={ state.step > 0 } class="action-btn tut-action-btn" data-action="prevStep">Back</button>
      <button if={ state.step < onboardingSteps().length - 1 } class="action-btn primary tut-action-btn" data-action="nextStep">Next</button>
      <button if={ state.step === onboardingSteps().length - 1 } class="action-btn primary tut-action-btn" data-action="finishOnboarding">Start playing</button>
    </div>
    <div class="tut-progress">
      <div each={ (s, i) in onboardingSteps() } class={ 'tut-dot ' + (i === state.step ? 'active' : i < state.step ? 'done' : '') }></div>
    </div>
  </div>

  <!-- Quick reference: toggled by the ? button for returning players -->
  <div if={ state.mode === 'hints' } class="tut-card tut-hints-card { state.suppressed ? 'is-suppressed' : '' }" role="complementary" aria-label="Quick reference" onclick={ onOverlayClick }>
    <div class="tut-header">
      <div class="tut-title-block">
        <span class="tut-badge">Quick reference</span>
        <span class="tut-subtitle">Fast scene controls and survival cues.</span>
      </div>
      <button class="tut-close" data-action="closeHints" data-tip="Dismiss guide">✕</button>
    </div>
    <ul class="hint-list">
      <li><span class="hint-key">Act</span><span class="hint-copy">Type natural language. Precise intent produces stronger outcomes.</span></li>
      <li><span class="hint-key">Inspect</span><span class="hint-copy">Tap player card, companions, NPCs, and items for sheets and actions.</span></li>
      <li><span class="hint-key">Combat</span><span class="hint-copy">Resolve rolls inside the combat modal. Return to scene when done.</span></li>
      <li><span class="hint-key">Vitals</span><span class="hint-copy">Vitality, Spirit, Essence. Essence is precious and slow to recover.</span></li>
      <li><span class="hint-key">Navigate</span><span class="hint-copy">Left: field log. Right: quests and atlas map. Swipe to toggle on mobile.</span></li>
      <li><span class="hint-key">World</span><span class="hint-copy">The world keeps moving between your actions. Check the world log.</span></li>
    </ul>
    <button class="action-btn tut-action-btn tut-more-btn" data-action="openFullGuide">Full guide</button>
  </div>

  <script>
    import { dispatcher } from '../../app-states.js'
    import { TOPICS } from "@anyquest/shared-core"
    import { hasAnyAIKey } from '../../ai/config.js'

    const ONBOARDING_DONE_KEY = 'am2_onboarding_done'
    const HINTS_SEEN_KEY = 'am2_hints_seen'

    const ONBOARDING_STEPS = [
      {
        title: 'Your world is alive',
        body: 'Everything you see was forged by AI from your world seed. NPCs move, trade, and plot independently. Quests evolve and the environment decays even when you aren\'t watching. You are part of a living, breathing chronicle.',
      },
      {
        title: 'Speak your intent',
        body: 'AnyQuest uses a "Yes, And" engine. Type anything you want to do — "bribe the guard with my ring", "climb the window to sneak past", "improvise a fire with the dry wood". The AI evaluates your plan against your character\'s skills and the world\'s rules.',
      },
      {
        title: 'Inspect everything',
        body: 'Tap your player card, companions, NPCs, or items to reveal detailed sheets. You can find hidden motives, review past memories, and trigger specific actions like "Trade", "Talk", or "Use" directly from these views.',
      },
      {
        title: '3D Spatial Awareness',
        body: 'The world has vertical depth. Use the Atlas Map to navigate North, East, South, West, and now UP or DOWN. Discover spires, dungeons, and multi-level structures. Tap nodes on the map to see intel on visited locations.',
      },
      {
        title: 'Tactical Combat',
        body: 'Battle triggers a dedicated modal. Choose from Attack, unique Abilities, or Inventory items. Every action is resolved via a D20 Dice Wizard. Watch your Vitality, Spirit, and Essence closely — defeat has permanent consequences.',
      },
      {
        title: 'Beta Access & Quotas',
        body: 'As a closed tester, you have a "Tiny Daily Quota" of managed AI actions. If you deplete your daily allowance, you can continue playing seamlessly by adding your own API key in Settings or running Ollama locally.',
      },
    ]

    export default {
      onOverlayClick(e) {
        const btn = e.target.closest('[data-action]');
        if (!btn) return;
        const action = btn.dataset.action;
        if (typeof this[action] === 'function') this[action](e);
      },
      state: { mode: null, step: 0, suppressed: false },

      onMounted() {
        this.handles = [
          dispatcher.on(TOPICS.game.started,     () => { this.onGameReady(true) }),
          dispatcher.on(TOPICS.game.loaded,      () => { this.onGameReady(false) }),
          dispatcher.on(TOPICS.ui.aiConfigSaved, () => { this.onAiConfigSaved() }),
          dispatcher.on(TOPICS.ui.tutorialStart, () => { this.toggleHints() }),
          dispatcher.on(TOPICS.ui.dialogOpen,    () => { this.handleDialogOpen() }),
          dispatcher.on(TOPICS.ui.dialogClose,   () => { this.handleDialogClose() }),
        ]
        this.syncSuppressedState()
        this.suppressionObserver = new MutationObserver(() => this.syncSuppressedState())
        this.suppressionObserver.observe(document.documentElement, {
          attributes: true,
          attributeFilter: ['class'],
          subtree: false,
        })
        this.suppressionObserver.observe(document.body, {
          attributes: true,
          attributeFilter: ['open', 'hidden', 'class'],
          childList: true,
          subtree: true,
        })
      },

      onUnmounted() {
        this.clearTimer()
        if (this.handles) this.handles.forEach(h => dispatcher.off(h))
        this.suppressionObserver?.disconnect?.()
      },

      onboardingSteps() { return ONBOARDING_STEPS },
      currentStep() { return ONBOARDING_STEPS[this.state.step] || ONBOARDING_STEPS[0] },

      onGameReady(isNewGame) {
        this.clearTimer()
        if (!hasAnyAIKey()) {
          this.update({ mode: 'aiSetup', step: 0 })
          return
        }
        // New game + never onboarded → show onboarding
        if (isNewGame && !localStorage.getItem(ONBOARDING_DONE_KEY)) {
          this.timer = setTimeout(() => {
            this.timer = null
            this.update({ mode: 'onboarding', step: 0 })
          }, 1200)
          return
        }
        // Returning player, first load → show hints briefly
        if (!localStorage.getItem(HINTS_SEEN_KEY)) {
          this.timer = setTimeout(() => {
            this.timer = null
            if (document.querySelector('dialog[open]')) return
            this.update({ mode: 'hints' })
          }, 2000)
        }
      },

      onAiConfigSaved() {
        if (!hasAnyAIKey()) return
        if (this.state.mode === 'aiSetup') this.update({ mode: null })
      },

      handleDialogOpen() {
        if (!this.state.suppressed) this.update({ suppressed: true })
        requestAnimationFrame(() => this.syncSuppressedState())
      },

      handleDialogClose() {
        requestAnimationFrame(() => this.syncSuppressedState())
      },

      syncSuppressedState() {
        const suppressed = document.documentElement.classList.contains('am2-modal-open')
          || !!document.querySelector('dialog[open], #game-modal[open]')
        if (suppressed === this.state.suppressed) return
        this.update({ suppressed })
      },

      clearTimer() {
        if (!this.timer) return
        clearTimeout(this.timer)
        this.timer = null
      },

      nextStep() {
        const next = Math.min(this.state.step + 1, ONBOARDING_STEPS.length - 1)
        this.update({ step: next })
      },

      prevStep() {
        this.update({ step: Math.max(0, this.state.step - 1) })
      },

      finishOnboarding() {
        this.clearTimer()
        localStorage.setItem(ONBOARDING_DONE_KEY, '1')
        localStorage.setItem(HINTS_SEEN_KEY, '1')
        this.update({ mode: null, step: 0 })
      },

      toggleHints() {
        this.clearTimer()
        this.update({ mode: this.state.mode === 'hints' ? null : 'hints', step: 0 })
      },

      closeHints() {
        this.clearTimer()
        localStorage.setItem(HINTS_SEEN_KEY, '1')
        this.update({ mode: null })
      },

      dismissAI() {
        this.clearTimer()
        this.update({ mode: null })
      },

      openSettings() {
        dispatcher.trigger('tutorial-overlay', TOPICS.ui.dialogOpen, { content: 'settings-dialog', data: { initialTab: 'advanced' } })
        this.update({ mode: null })
      },

      openFullGuide() {
        this.clearTimer()
        localStorage.setItem(HINTS_SEEN_KEY, '1')
        this.update({ mode: null })
        dispatcher.trigger('tutorial-overlay', TOPICS.ui.dialogOpen, { content: 'how-to-play-dialog', data: {}, options: { variant: 'notebook' } })
      },
    }
  </script>

  <style>
    tutorial-overlay { display: contents; }

    .tut-card {
      position: fixed;
      z-index: var(--z-tutorial, 2500);
      width: min(25rem, 92vw);
      background:
        linear-gradient(180deg, color-mix(in srgb, var(--surface-raised) var(--alpha-subtle), var(--bg)), color-mix(in srgb, var(--bg) var(--alpha-high), var(--surface))),
        radial-gradient(circle at top right, color-mix(in srgb, var(--gold) var(--alpha-glow), transparent), transparent var(--alpha-ghost));
      border: 1px solid color-mix(in srgb, var(--gold) 40%, var(--border));
      border-radius: 1rem;
      padding: var(--space-md) 1.1rem 0.9rem;
      box-shadow:
        0 1.5rem 3rem rgba(0,0,0,0.5),
        0 0 0 1px color-mix(in srgb, var(--gold) var(--alpha-glow), transparent),
        inset 0 1px 0 color-mix(in srgb, #f8fbff var(--alpha-subtle), transparent);
      pointer-events: all;
      animation: tut-in 0.22s ease-out;
      backdrop-filter: blur(1rem);
    }

    .tut-card.is-suppressed {
      opacity: 0;
      visibility: hidden;
      pointer-events: none;
      transform: translateY(0.5rem) scale(0.97);
      transition: opacity 0s linear, transform 0.16s ease, visibility 0s linear;
    }


    .tut-center {
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }

    /* Onboarding: centered, generous sizing */
    .tut-onboarding {
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: min(27.5rem, 92vw);
    }

    /* Quick reference: top-right corner */
    .tut-hints-card {
      top: calc(2.75rem + var(--space-sm) + env(safe-area-inset-top, 0));
      right: max(var(--space-sm), env(safe-area-inset-right, 0));
      left: auto;
      bottom: auto;
      transform: none;
      width: min(25rem, 92vw);
      max-height: min(44vh, 19.5rem);
      overflow-y: auto;
      scrollbar-gutter: stable;
    }

    .tut-header {
      display: flex;
      align-items: center;
      gap: 0.6rem;
      margin-bottom: 0.6rem;
    }

    .tut-title-block {
      display: grid;
      gap: 0.12rem;
    }

    .tut-subtitle {
      font-size: 0.68rem;
      letter-spacing: 0.05em;
      color: color-mix(in srgb, var(--text-muted) 88%, #fff);
      text-transform: uppercase;
    }

    .tut-badge {
      font-size: var(--type-3xs);
      letter-spacing: 0.14em;
      color: var(--gold);
      text-transform: uppercase;
      font-weight: 800;
    }

    .tut-step-count {
      font-size: var(--type-3xs);
      color: var(--text-dim);
      letter-spacing: 0.06em;
      margin-left: auto;
    }

    .tut-close {
      background: color-mix(in srgb, var(--surface) var(--alpha-focus), transparent);
      border: 1px solid color-mix(in srgb, var(--border) var(--alpha-high), transparent);
      color: var(--text-dim);
      cursor: pointer;
      font-size: var(--space-sm);
      width: 1.9rem;
      height: 1.9rem;
      border-radius: 62.4375rem;
      padding: 0;
      line-height: 1;
      flex: 0 0 auto;
      &:hover {
        color: var(--text-prose);
        border-color: color-mix(in srgb, var(--gold) 40%, transparent);
      }
    }

    .tut-title {
      font-size: 0.82rem;
      letter-spacing: 0.08em;
      color: var(--gold);
      margin: 0 0 var(--space-xs);
    }

    .tut-body {
      font-size: var(--space-sm);
      color: var(--text-muted);
      line-height: 1.55;
      margin: 0 0 var(--space-sm);
    }

    .tut-actions {
      display: flex;
      gap: var(--space-xs);
      flex-wrap: wrap;
    }

    .tut-action-btn {
      font-size: 0.68rem !important;
      padding: var(--space-xs) var(--space-sm) !important;
      flex: 1;
      min-width: 0;
    }

    /* Onboarding step content */
    .tut-step {
      padding: var(--space-xs) 0;
    }

    .tut-step-title {
      font-size: 0.88rem;
      letter-spacing: 0.06em;
      color: var(--gold);
      margin: 0 0 var(--space-xs);
      font-weight: 700;
    }

    .tut-step-body {
      font-size: 0.82rem;
      color: var(--text-muted);
      line-height: 1.6;
      margin: 0 0 var(--space-sm);
    }

    .tut-progress {
      display: flex;
      justify-content: center;
      gap: var(--space-xs);
      margin-top: var(--space-xs);
    }

    .tut-dot {
      width: 0.5rem;
      height: 0.5rem;
      border-radius: 62.4375rem;
      background: color-mix(in srgb, var(--border) 60%, transparent);
      transition: background 0.2s, transform 0.2s;
    }

    .tut-dot.active {
      background: var(--gold);
      transform: scale(1.25);
    }

    .tut-dot.done {
      background: color-mix(in srgb, var(--gold) 50%, var(--border));
    }

    /* Quick reference hints */
    .hint-list {
      list-style: none;
      padding: 0;
      margin: 0 0 0.6rem;
      display: grid;
      gap: var(--space-xs);
    }

    .hint-list li {
      font-size: var(--space-sm);
      color: var(--text-muted);
      line-height: 1.5;
      display: grid;
      grid-template-columns: 4.5rem 1fr;
      gap: var(--space-xs);
      align-items: start;
      padding: var(--space-xs) var(--space-xs);
      border: 1px solid color-mix(in srgb, var(--border) 70%, transparent);
      border-radius: 0.625rem;
      background: color-mix(in srgb, var(--surface-card-2) var(--alpha-ghost), transparent);
    }

    .hint-key {
      font-size: 0.66rem;
      letter-spacing: 0.1em;
      color: var(--gold);
      font-weight: 700;
      white-space: nowrap;
      text-transform: uppercase;
      padding-top: 0.06rem;
    }

    .hint-copy {
      color: color-mix(in srgb, var(--text-prose) var(--alpha-subtle), var(--text-muted));
    }

    .tut-more-btn {
      width: 100%;
      text-align: center;
      min-height: 2.1rem;
    }

    .tut-inline,
    .tut-inline-link {
      display: inline-flex;
      align-items: center;
      border: 1px solid color-mix(in srgb, var(--gold) var(--alpha-subtle), transparent);
      border-radius: 62.4375rem;
      padding: 0.02rem var(--space-xs);
      color: color-mix(in srgb, var(--gold) var(--alpha-high), var(--text-prose));
      background: color-mix(in srgb, var(--gold-glow) var(--alpha-ghost), transparent);
      font-family: var(--font-system, 'Inter', sans-serif);
      font-size: 0.82em;
      letter-spacing: 0.04em;
      font-weight: 600;
      line-height: 1.2;
      vertical-align: baseline;
    }

    .tut-inline-link {
      text-decoration: underline;
      text-underline-offset: 0.125rem;
    }

    @media (min-width: 67.5rem) {
      .hint-list {
        grid-template-columns: repeat(2, minmax(0, 1fr));
      }
    }

    @media (max-width: 49.9375rem) {
      .tut-hints-card {
        top: calc(2.75rem + 0.6rem + env(safe-area-inset-top, 0));
        right: max(var(--space-xs), env(safe-area-inset-right, 0));
        left: max(var(--space-xs), env(safe-area-inset-left, 0));
        width: auto;
        max-height: min(40vh, 21.25rem);
      }

      .hint-list li {
        grid-template-columns: 4rem 1fr;
        gap: var(--space-xs);
        padding: var(--space-xs) var(--space-xs);
        font-size: var(--space-sm);
      }

      .hint-key {
        font-size: var(--type-3xs);
      }

      .tut-onboarding {
        width: 92vw;
      }
    }
  </style>
</tutorial-overlay>
